import { useEffect, useState } from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import type { OrganizationContext } from "../types";
import { StatusState } from "../adaptive/primitives";
import { SalonsAgenda } from "./SalonsAgenda";
import { SalonsInbox } from "./SalonsInbox";
import { SalonsDemoSimulator } from "./SalonsDemoSimulator";
import { SalonsCustomers } from "./SalonsCustomers";
import { SalonsHome } from "./SalonsHome";
import { SalonsReports } from "./SalonsReports";
import { SalonsSales } from "./SalonsSales";
import { SalonsSettings } from "./SalonsSettings";
import type { SalonMercadoPagoConnection, SalonOverview } from "./types";

type SectionKey = "home" | "agenda" | "conversations" | "customers" | "sales" | "reports" | "settings";

function sectionFromPath(nestedPath: string): SectionKey {
  const parts = nestedPath.replace(/^\/+/, "").split("/").filter(Boolean);
  const value = parts[0] ?? "";
  if (value === "" || value === "home") return "home";
  if (value === "settings" || value === "services") return "settings";
  if (value === "agenda" || value === "conversations" || value === "customers" || value === "sales" || value === "reports") return value;
  return "home";
}

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudo completar la operación.";
}

function DeferredDomain({ eyebrow, title, message, detail }: {
  eyebrow: string;
  title: string;
  message: string;
  detail: string;
}) {
  return <section className="cd-salons-placeholder" aria-labelledby={`salons-${title.toLowerCase()}-title`}>
    <p className="cd-page-header__eyebrow">{eyebrow}</p>
    <h1 id={`salons-${title.toLowerCase()}-title`} className="cd-page-header__title">{title}</h1>
    <p>{message}</p>
    <p><strong>Estado:</strong> {detail}</p>
  </section>;
}

export function SalonsProduct({ organization, nestedPath, api, navigate, onExpired, basePath }: {
  organization: OrganizationContext;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
  basePath?: string;
}) {
  const section = sectionFromPath(nestedPath);
  const [overview, setOverview] = useState<SalonOverview | null>(null);
  const [mercadoPago, setMercadoPago] = useState<SalonMercadoPagoConnection | null>(null);
  const [mercadoPagoError, setMercadoPagoError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const orgId = organization.organization.id;
  const runtimeBasePath = basePath ?? `/o/${organization.organization.slug}/salons`;

  const load = async () => {
    try {
      setError(null);
      const nextOverview = await api.salonsOverview(orgId);
      setOverview(nextOverview);
      try {
        setMercadoPagoError(null);
        setMercadoPago(await api.salonMercadoPagoConnection(orgId));
      } catch (cause) {
        if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
        else {
          setMercadoPago(null);
          setMercadoPagoError(errorText(cause));
        }
      }
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorText(cause));
    }
  };

  useEffect(() => { void load(); }, [orgId]);

  async function refresh() {
    setRefreshing(true);
    try { await load(); }
    finally { setRefreshing(false); }
  }

  if (!overview && !error) return <StatusState title="Cargando Peluquerías…" message="Leyendo agenda, clientes y conversaciones." tone="loading" />;
  if (!overview) return <StatusState title="No se pudo abrir Peluquerías" message={error ?? "Error desconocido"} tone="danger" action={<button onClick={() => void load()}>Reintentar</button>} />;

  if (section === "home") return <>
    {error && <div className="inline-alert" role="alert">{error}</div>}
    <SalonsHome
      overview={overview}
      organizationName={organization.organization.displayName}
      mercadoPago={mercadoPago}
      mercadoPagoError={mercadoPagoError}
      navigate={navigate}
      basePath={runtimeBasePath}
      onRefresh={() => void refresh()}
      refreshing={refreshing}
    />
  </>;

  if (section === "conversations" && nestedPath.replace(/^\/+/, "").startsWith("conversations/simulator") && runtimeBasePath.startsWith("/demos/")) {
    return <SalonsDemoSimulator organizationId={orgId} api={api} navigate={navigate} basePath={runtimeBasePath} />;
  }

  if (section === "sales") return <SalonsSales
    organizationId={orgId}
    overview={overview}
    api={api}
    navigate={navigate}
    basePath={runtimeBasePath}
  />;

  if (section === "reports") return <SalonsReports
    organizationId={orgId}
    overview={overview}
    api={api}
  />;

  if (section === "settings") return <>
    {error && <div className="inline-alert" role="alert">{error}</div>}
    <SalonsSettings
      organization={organization}
      nestedPath={nestedPath}
      overview={overview}
      api={api}
      navigate={navigate}
      onExpired={onExpired}
      basePath={runtimeBasePath}
    />
  </>;

  return <div className="cd-salons-product">
    {error && <div className="inline-alert" role="alert">{error}</div>}

    {section === "agenda" && <SalonsAgenda
      organizationId={orgId}
      overview={overview}
      api={api}
      navigate={navigate}
      basePath={runtimeBasePath}
      onOverviewRefresh={load}
    />}

    {section === "conversations" && <SalonsInbox
      organizationId={orgId}
      nestedPath={nestedPath}
      api={api}
      navigate={navigate}
      basePath={runtimeBasePath}
    />}

    {section === "customers" && <SalonsCustomers
      organizationId={orgId}
      nestedPath={nestedPath}
      api={api}
      navigate={navigate}
      basePath={runtimeBasePath}
    />}

  </div>;
}
